import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, XCircle, Loader } from 'lucide-react';
import { usePaymentOrderStatus } from '../../hooks/usePayment';
import { useRefreshSubscription } from '../../hooks/useSubscription';
import { useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import styles from './PaymentSuccess.module.css';

const PaymentSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const orderId = searchParams.get('order_id');
  const [paymentStatus, setPaymentStatus] = useState('checking');
  const [errorMessage, setErrorMessage] = useState('');
  const [isRefreshing, setIsRefreshing] = useState(false);
  const { refreshSubscription } = useRefreshSubscription();
  const queryClient = useQueryClient();

  const { data: paymentData, error, refetch } = usePaymentOrderStatus(orderId);

  // track polling interval and processed terminal state to avoid duplicate side-effects/toasts
  const pollingIntervalRef = useRef(null);
  const processedStatusRef = useRef(null);

  // Refetch payment status every 5 seconds to check for updates
  useEffect(() => {
    if (!orderId) return;
    // store interval id to allow manual clear on terminal states
    pollingIntervalRef.current = setInterval(() => {
      refetch();
    }, 5000);

    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, [orderId, refetch]);

  // reset processed state when orderId changes
  useEffect(() => {
    processedStatusRef.current = null;
  }, [orderId]);

  useEffect(() => {
    if (!orderId) {
      setPaymentStatus('error');
      setErrorMessage('Order ID not found');
      toast.error('Order ID not found', { toastId: 'order-id-missing' });
      return;
    }

    if (error) {
      setPaymentStatus('error');
      setErrorMessage('Failed to verify payment status. Please try again.');
      toast.error('Failed to verify payment status', { toastId: 'verify-payment-failed' });
      return;
    }

    if (paymentData) {
      console.log('Payment Data:', paymentData);
      const orderStatus = paymentData.order?.order_status;
      const paymentStatusArray = paymentData.payment_status;

      // Get the latest payment status (most recent one)
      let latestPaymentStatus = null;
      let latestPaymentDetails = null;
      
      if (paymentStatusArray && paymentStatusArray.length > 0) {
        // Sort by payment_time to get the most recent
        const sortedPayments = paymentStatusArray.sort((a, b) => 
          new Date(b.payment_time) - new Date(a.payment_time)
        );
        latestPaymentDetails = sortedPayments[0];
        latestPaymentStatus = latestPaymentDetails.payment_status;
      }
      
      // Determine the final status based on both order and payment status
      let finalStatus = 'pending';
      
      if (latestPaymentStatus === 'SUCCESS' || latestPaymentStatus === 'COMPLETED') {
        finalStatus = 'success';
      } else if (latestPaymentStatus === 'FAILED' || latestPaymentStatus === 'CANCELLED' || latestPaymentStatus === 'USER_DROPPED') {
        finalStatus = 'failed';
      } else if (latestPaymentStatus === 'PENDING') {
        finalStatus = 'pending';
      } else if (orderStatus === 'SUCCESS' || orderStatus === 'PAID' || orderStatus === 'COMPLETED') {
        finalStatus = 'success';
      } else if (orderStatus === 'FAILED' || orderStatus === 'CANCELLED') {
        finalStatus = 'failed';
      } else if (orderStatus === 'CREATED' && !latestPaymentStatus) {
        finalStatus = 'pending';
      } else if (orderStatus === 'CREATED' && latestPaymentStatus === 'FAILED') {
        finalStatus = 'failed';
      } else if (orderStatus === 'CREATED' && latestPaymentStatus === 'USER_DROPPED') {
        finalStatus = 'failed';
      } else if (orderStatus === 'CREATED' && latestPaymentStatus === 'PENDING') {
        finalStatus = 'pending';
      }
      
      console.log('Final Status:', finalStatus);
      
      if (finalStatus === 'success') {
        if (processedStatusRef.current === 'success') return; // already handled
        processedStatusRef.current = 'success';
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }
        setPaymentStatus('success');
        setErrorMessage('');
        
        // Immediately clear cache and refresh subscription data
        queryClient.removeQueries('userSubscription');
        refreshSubscription();
        
        // Also add a delayed refresh to ensure backend processing is complete
        setTimeout(() => {
          refreshSubscription();
        }, 2000);
        
        toast.success('Payment successful! Your subscription is now active.', { toastId: `payment-success-${orderId}` });
        // Automatically navigate to home after 3 seconds
        setTimeout(() => {
          navigate('/');
        }, 3000);
      } else if (finalStatus === 'failed') {
        if (processedStatusRef.current === 'failed') return; // already handled
        processedStatusRef.current = 'failed';
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }
        setPaymentStatus('failed');
        
        // Get specific error details if available
        let specificError = 'Payment was not successful. Please try again.';
        if (latestPaymentDetails) {
          if (latestPaymentDetails.error_details) {
            specificError = latestPaymentDetails.error_details.error_description || specificError;
          } else if (latestPaymentStatus === 'USER_DROPPED') {
            specificError = 'Payment was cancelled by the user.';
          }
        }
        
        setErrorMessage(specificError);
        toast.error('Payment failed. Please try again.', { toastId: `payment-failed-${orderId}` });
      } else {
        setPaymentStatus('pending');
        
        // Get appropriate pending message based on the latest status
        let pendingMessage = 'Payment is being processed. Please wait...';
        if (latestPaymentStatus === 'PENDING') {
          pendingMessage = 'Payment is currently pending. Please wait for confirmation...';
        } else if (orderStatus === 'CREATED' && !latestPaymentStatus) {
          pendingMessage = 'Payment session created. Please complete the payment...';
        }
        
        setErrorMessage(pendingMessage);
      }
    }
  }, [paymentData, orderId, error, navigate, refreshSubscription, queryClient]);

  const handleContinue = () => {
    navigate('/');
  };

  const handleRetry = () => {
    navigate('/subscription-plans');
  };

  const handleRefreshSubscription = async () => {
    setIsRefreshing(true);
    try {
      await refreshSubscription();
      toast.success('Subscription status refreshed successfully!');
    } catch (error) {
      toast.error('Failed to refresh subscription status');
    } finally {
      setIsRefreshing(false);
    }
  };

  if (!orderId) {
    return (
      <div className={styles.container}>
        <div className={styles.card}>
          <XCircle className={styles.errorIcon} />
          <h2>Invalid Payment</h2>
          <p>Order ID not found. Please try again.</p>
          <button onClick={handleRetry} className={styles.button}>
            Go to Subscription Plans
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.card}>
        {paymentStatus === 'checking' && (
          <>
            <Loader className={styles.loadingIcon} />
            <h2>Verifying Payment</h2>
            <p>Please wait while we verify your payment...</p>
          </>
        )}

        {paymentStatus === 'success' && (
          <>
            <CheckCircle className={styles.successIcon} />
            <h2>Payment Successful!</h2>
            <p>Your subscription has been activated successfully.</p>
            <p>Redirecting to home page in 3 seconds...</p>
            <div className={styles.details}>
              <p><strong>Order ID:</strong> {orderId}</p>
              <p><strong>Status:</strong> Active</p>
            </div>
            <div className={styles.buttonGroup}>
              <button onClick={handleContinue} className={styles.button}>
                Continue to App
              </button>
              <button 
                onClick={handleRefreshSubscription} 
                disabled={isRefreshing}
                className={`${styles.button} ${styles.secondaryButton}`}
              >
                {isRefreshing ? 'Refreshing...' : 'Refresh Subscription Status'}
              </button>
            </div>
          </>
        )}

        {paymentStatus === 'failed' && (
          <>
            <XCircle className={styles.errorIcon} />
            <h2>Payment Failed</h2>
            <p>{errorMessage}</p>
            <div className={styles.details}>
              <p><strong>Order ID:</strong> {orderId}</p>
              <p><strong>Status:</strong> Failed</p>
            </div>
            <button onClick={handleRetry} className={styles.button}>
              Try Again
            </button>
          </>
        )}

        {paymentStatus === 'pending' && (
          <>
            <Loader className={styles.loadingIcon} />
            <h2>Payment Pending</h2>
            <p>{errorMessage}</p>
            <div className={styles.details}>
              <p><strong>Order ID:</strong> {orderId}</p>
              <p><strong>Status:</strong> Processing</p>
            </div>
          </>
        )}

        {paymentStatus === 'error' && (
          <>
            <XCircle className={styles.errorIcon} />
            <h2>Error</h2>
            <p>{errorMessage}</p>
            <div className={styles.details}>
              <p><strong>Order ID:</strong> {orderId || 'Not found'}</p>
              <p><strong>Status:</strong> Error</p>
            </div>
            <button onClick={handleRetry} className={styles.button}>
              Go to Subscription Plans
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default PaymentSuccess; 